import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, ClipboardCheck, Search, Plus, Loader2, MapPin, 
  Calendar, Camera, User, CheckCircle, AlertTriangle, Clock,
  Play, Upload, Image as ImageIcon, X
} from "lucide-react";
import { API } from "@/App";

const inspectionTypes = [
  { value: "move_in", label: "Move-In Inspection" },
  { value: "move_out", label: "Move-Out Inspection" },
  { value: "periodic", label: "Periodic Inspection" },
  { value: "gas_safety", label: "Gas Safety Check" },
  { value: "electrical", label: "Electrical Inspection" },
  { value: "general", label: "General Inspection" }
];

const statusConfig = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700", icon: Calendar },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700", icon: Play },
  completed: { label: "Completed", color: "bg-purple-100 text-purple-700", icon: ClipboardCheck },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", icon: CheckCircle },
  requires_work: { label: "Requires Work", color: "bg-red-100 text-red-700", icon: AlertTriangle }
};

export const InspectionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [inspections, setInspections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  
  // Create modal
  const [createModal, setCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newInspection, setNewInspection] = useState({
    property_reference: searchParams.get("property") || "",
    inspection_type: "periodic",
    scheduled_date: "",
    notes: ""
  });

  // Detail modal
  const [detailModal, setDetailModal] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [photoRoom, setPhotoRoom] = useState("");
  const [photoDesc, setPhotoDesc] = useState("");

  // Approval modal
  const [approvalModal, setApprovalModal] = useState(false);
  const [approvalData, setApprovalData] = useState({
    approved: true,
    requires_work: false,
    work_items: [""],
    notes: ""
  });

  useEffect(() => {
    fetchData();
    if (searchParams.get("property")) {
      setCreateModal(true);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [inspRes, propRes] = await Promise.all([
        axios.get(`${API}/inspections`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInspections(inspRes.data.inspections || []);
      setProperties(propRes.data.properties || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newInspection.property_reference || !newInspection.scheduled_date) {
      toast.error("Please select a property and date");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/inspections`, newInspection, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Inspection scheduled");
      setCreateModal(false);
      setNewInspection({ property_reference: "", inspection_type: "periodic", scheduled_date: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create inspection");
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (inspection) => {
    try {
      const response = await axios.get(`${API}/inspections/${inspection.inspection_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInspection(response.data.inspection);
      setDetailModal(true);
    } catch (error) {
      toast.error("Failed to load inspection");
    }
  };

  const handleStartInspection = async () => {
    try {
      await axios.post(`${API}/inspections/${selectedInspection.inspection_id}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Inspection started");
      setSelectedInspection({ ...selectedInspection, status: "in_progress" });
      fetchData();
    } catch (error) {
      toast.error("Failed to start inspection");
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !photoRoom) {
      toast.error("Please enter a room name");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("room_name", photoRoom);
    formData.append("description", photoDesc);

    try {
      await axios.post(`${API}/inspections/${selectedInspection.inspection_id}/photos`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Photo uploaded");
      setPhotoRoom("");
      setPhotoDesc("");
      // Refresh inspection
      const response = await axios.get(`${API}/inspections/${selectedInspection.inspection_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInspection(response.data.inspection);
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCompleteInspection = async () => {
    try {
      await axios.post(`${API}/inspections/${selectedInspection.inspection_id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Inspection completed, landlord notified");
      setSelectedInspection({ ...selectedInspection, status: "completed" });
      fetchData();
    } catch (error) {
      toast.error("Failed to complete inspection");
    }
  };

  const openApprovalModal = () => {
    setApprovalData({ approved: true, requires_work: false, work_items: [""], notes: "" });
    setApprovalModal(true);
  };

  const handleApprove = async () => {
    try {
      const data = {
        ...approvalData,
        work_items: approvalData.work_items.filter(w => w.trim())
      };
      await axios.post(`${API}/inspections/${selectedInspection.inspection_id}/approve`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(data.requires_work ? "Work items created as jobs" : "Inspection approved");
      setApprovalModal(false);
      setDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to process approval");
    }
  };

  const addWorkItem = () => {
    setApprovalData({ ...approvalData, work_items: [...approvalData.work_items, ""] });
  };

  const updateWorkItem = (index, value) => {
    const items = [...approvalData.work_items];
    items[index] = value;
    setApprovalData({ ...approvalData, work_items: items });
  };

  const removeWorkItem = (index) => {
    const items = approvalData.work_items.filter((_, i) => i !== index);
    setApprovalData({ ...approvalData, work_items: items.length ? items : [""] });
  };

  const filteredInspections = inspections.filter(i => {
    const matchesSearch = 
      i.property_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.property_reference?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    return matchesSearch && i.status === activeTab;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold font-outfit">Inspections</h1>
                <p className="text-sm text-muted-foreground">{inspections.length} inspections</p>
              </div>
            </div>
            
            {(user.role === "admin" || user.role === "contractor") && (
              <Button onClick={() => setCreateModal(true)} data-testid="new-inspection-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Inspection
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by address or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="all">All ({inspections.length})</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
            <TabsTrigger value="in_progress">In Progress</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
            <TabsTrigger value="requires_work">Needs Work</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {filteredInspections.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">No inspections found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredInspections.map((inspection) => {
                  const status = statusConfig[inspection.status] || statusConfig.scheduled;
                  const StatusIcon = status.icon;
                  
                  return (
                    <Card 
                      key={inspection.inspection_id}
                      className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
                      onClick={() => openDetail(inspection)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <Badge variant="outline" className="text-xs">
                            #{inspection.property_reference}
                          </Badge>
                          <Badge className={status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        
                        <h3 className="font-semibold mb-1 line-clamp-2">
                          {inspection.property_address}
                        </h3>
                        
                        <p className="text-sm text-muted-foreground mb-3">
                          {inspectionTypes.find(t => t.value === inspection.inspection_type)?.label}
                        </p>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {inspection.scheduled_date}
                          </span>
                          {inspection.photos?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Camera className="w-4 h-4" />
                              {inspection.photos.length}
                            </span>
                          )}
                        </div>

                        {inspection.contractor_name && (
                          <p className="text-sm mt-3 pt-3 border-t flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" />
                            {inspection.contractor_name}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Inspection Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule New Inspection</DialogTitle>
            <DialogDescription>
              Select a property and date for the inspection
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Property</Label>
              <Select
                value={newInspection.property_reference}
                onValueChange={(v) => setNewInspection({ ...newInspection, property_reference: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.reference} value={p.reference}>
                      {p.reference} - {p.display_address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Inspection Type</Label>
              <Select
                value={newInspection.inspection_type}
                onValueChange={(v) => setNewInspection({ ...newInspection, inspection_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {inspectionTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Scheduled Date</Label>
              <Input
                type="date"
                value={newInspection.scheduled_date}
                onChange={(e) => setNewInspection({ ...newInspection, scheduled_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any special instructions..."
                value={newInspection.notes}
                onChange={(e) => setNewInspection({ ...newInspection, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Schedule Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inspection Detail Modal */}
      <Dialog open={detailModal} onOpenChange={setDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedInspection && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5" />
                  {inspectionTypes.find(t => t.value === selectedInspection.inspection_type)?.label}
                </DialogTitle>
                <DialogDescription>
                  {selectedInspection.property_address}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <Badge className={statusConfig[selectedInspection.status]?.color}>
                    {statusConfig[selectedInspection.status]?.label}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Scheduled: {selectedInspection.scheduled_date}
                  </span>
                </div>

                {/* Contractor Actions */}
                {user.role === "contractor" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    {selectedInspection.status === "scheduled" && (
                      <Button onClick={handleStartInspection} className="w-full">
                        <Play className="w-4 h-4 mr-2" />
                        Start Inspection
                      </Button>
                    )}

                    {selectedInspection.status === "in_progress" && (
                      <>
                        {/* Photo Upload */}
                        <div className="space-y-2">
                          <Label>Upload Photo</Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Room name (e.g., Kitchen)"
                              value={photoRoom}
                              onChange={(e) => setPhotoRoom(e.target.value)}
                              className="flex-1"
                            />
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleUploadPhoto}
                              accept="image/*"
                              className="hidden"
                            />
                            <Button 
                              variant="outline"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading || !photoRoom}
                            >
                              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        <Button 
                          onClick={handleCompleteInspection} 
                          className="w-full"
                          disabled={!selectedInspection.photos?.length}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Complete Inspection
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* Landlord Actions */}
                {(user.role === "landlord" || user.role === "admin") && selectedInspection.status === "completed" && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-700 mb-3">
                      This inspection is ready for your review and approval.
                    </p>
                    <Button onClick={openApprovalModal} className="w-full">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Review & Approve
                    </Button>
                  </div>
                )}

                {/* Photos */}
                {selectedInspection.photos?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Photos ({selectedInspection.photos.length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedInspection.photos.map((photo) => (
                        <div key={photo.photo_id} className="relative group">
                          <div className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-slate-400" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 rounded-b-lg">
                            {photo.room_name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Work Items (if requires work) */}
                {selectedInspection.requires_work && selectedInspection.work_items?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-700 mb-2">Work Required</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {selectedInspection.work_items.map((item, i) => (
                        <li key={i} className="text-sm">{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Notes */}
                {selectedInspection.notes && (
                  <div>
                    <h4 className="font-semibold mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground bg-slate-50 p-3 rounded-lg">
                      {selectedInspection.notes}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Modal */}
      <Dialog open={approvalModal} onOpenChange={setApprovalModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Inspection</DialogTitle>
            <DialogDescription>
              Approve the inspection or request work to be done
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex gap-4">
              <Button
                variant={!approvalData.requires_work ? "default" : "outline"}
                onClick={() => setApprovalData({ ...approvalData, approved: true, requires_work: false })}
                className="flex-1"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button
                variant={approvalData.requires_work ? "default" : "outline"}
                onClick={() => setApprovalData({ ...approvalData, approved: true, requires_work: true })}
                className="flex-1"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Requires Work
              </Button>
            </div>

            {approvalData.requires_work && (
              <div className="space-y-2">
                <Label>Work Items (will create jobs)</Label>
                {approvalData.work_items.map((item, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      placeholder="Describe work needed..."
                      value={item}
                      onChange={(e) => updateWorkItem(i, e.target.value)}
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeWorkItem(i)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addWorkItem}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Work Item
                </Button>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any additional comments..."
                value={approvalData.notes}
                onChange={(e) => setApprovalData({ ...approvalData, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalModal(false)}>Cancel</Button>
            <Button onClick={handleApprove}>
              {approvalData.requires_work ? "Create Jobs" : "Approve Inspection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
